// Libs
import { TFunction } from 'i18next';
import * as yup from 'yup';
// Others
import { IAdditionalContact, IAddressCustomer, IFormCustomer } from '~/utils/interface/customer';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING, MAX_SIZE_FILE_UPLOAD, REG_EXP } from '~/utils/constants/common';
import { TypeLinkQuickBooksEnum } from '~/utils/enum/common';

export const schema = (t: TFunction, isEdit: boolean) => {
  return yup
    .object()
    .shape({
      avatarImage: yup
        .object()
        .shape({
          id: yup.string().trim().optional(),
          url: yup.string().trim().optional(),
          file: yup
            .mixed<File>()
            .optional()
            .test('fileSize', t('common_validate_file_size', { ns: 'common/base' }), (value) => {
              if (!value) return true;
              if (value && value?.size <= MAX_SIZE_FILE_UPLOAD) return true;
              return false;
            }),
        })
        .optional(),
      contacts: yup.string().trim().optional(),
      firstName: yup
        .string()
        .trim()
        .required(t('common_validate_field_required', { ns: 'common/base' })),
      lastName: yup
        .string()
        .trim()
        .required(t('common_validate_field_required', { ns: 'common/base' })),
      email: yup
        .string()
        .trim()
        .optional()
        .test('isValidEmail', t('common_validate_invalid_email', { ns: 'common/base' }), (value) => {
          if (value && !REG_EXP.EMAIL.test(value)) return false;
          return true;
        }),
      phoneNumber: yup
        .string()
        .trim()
        .optional()
        .test('isValidPhone', t('common_validate_invalid_phone_number', { ns: 'common/base' }), (value) => {
          return !value || REG_EXP.PHONE_NUMBER.test(value);
        }),
      customerContacts: yup
        .array()
        .of(
          yup.object().shape({
            name: yup.string().trim().optional(),
            email: yup
              .string()
              .trim()
              .optional()
              .test('isValidEmail', t('common_validate_invalid_email', { ns: 'common/base' }), (value) => {
                if (value && !REG_EXP.EMAIL.test(value)) return false;
                return true;
              }),
            phoneNumber: yup
              .string()
              .trim()
              .optional()
              .test(
                'isValidPhoneContact',
                t('common_validate_invalid_phone_number', { ns: 'common/base' }),
                (value) => {
                  return !value || REG_EXP.PHONE_NUMBER.test(value);
                }
              ),
          })
        )
        .optional(),
      addresses: yup
        .array()
        .of(
          yup.object().shape({
            address: yup
              .string()
              .trim()
              .required(t('common_validate_field_required', { ns: 'common/base' })),
            address2: yup.string().trim().optional(),
            city: yup
              .string()
              .trim()
              .required(t('common_validate_field_required', { ns: 'common/base' })),
            state: yup
              .string()
              .trim()
              .required(t('common_validate_field_required', { ns: 'common/base' })),
            zipCode: yup
              .string()
              .trim()
              .required(t('common_validate_field_required', { ns: 'common/base' })),
            country: yup
              .string()
              .trim()
              .required(t('common_validate_field_required', { ns: 'common/base' })),
            isBillingAddress: yup.boolean().optional(),
            latitude: yup.number().optional(),
            longitude: yup.number().optional(),
          })
        )
        .optional()
        .test(
          'isRequiredIfNotEmpty',
          t('common_validate_field_required', { ns: 'common/base' }),
          function (value) {
            if (value && value.length > DEFAULT_NUMBER_ZERO) {
              return value.every(
                (addr) => addr.address && addr.city && addr.state && addr.zipCode && addr.country
              );
            }
            return true;
          }
        ),
      billingAddress: yup.string().trim().optional(),
      billingAddress2: yup.string().trim().optional(),
      billingCity: yup.string().trim().optional(),
      billingState: yup.string().trim().optional(),
      billingZipCode: yup.string().trim().optional(),
      billingCountry: yup.string().trim().optional(),
      tag: yup
        .string()
        .trim()
        .required(t('common_validate_field_required', { ns: 'common/base' })),
      typeLinkQuickBook: yup
        .string()
        .nullable()
        .when([], {
          is: () => isEdit !== true,
          then: (schema) =>
            schema.notOneOf([EMPTY_STRING], t('common_validate_field_required', { ns: 'common/base' })),
          otherwise: (schema) => schema.optional(),
        }),

      quickBookCustomerId: yup
        .string()
        .nullable()
        .when('typeLinkQuickBook', {
          is: (value: string) => value === TypeLinkQuickBooksEnum.ADD_EXISTING && !isEdit,
          then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
          otherwise: (schema) => schema.optional(),
        }),
      syncToken: yup
        .string()
        .nullable()
        .when('typeLinkQuickBook', {
          is: (value: string) => value === TypeLinkQuickBooksEnum.ADD_EXISTING,
          then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
          otherwise: (schema) => schema.optional(),
        }),
    })
    .required();
};

export const defaultValuesForm: IFormCustomer = {
  avatarImage: {},
  contacts: EMPTY_STRING,
  firstName: EMPTY_STRING,
  lastName: EMPTY_STRING,
  email: EMPTY_STRING,
  phoneNumber: EMPTY_STRING,
  customerContacts: [
    {
      name: EMPTY_STRING,
      email: EMPTY_STRING,
      phoneNumber: EMPTY_STRING,
    },
  ],
  addresses: [
    {
      address: EMPTY_STRING,
      address2: EMPTY_STRING,
      city: EMPTY_STRING,
      state: EMPTY_STRING,
      zipCode: EMPTY_STRING,
      country: EMPTY_STRING,
      isBillingAddress: true,
    },
  ],
  billingAddress: EMPTY_STRING,
  billingAddress2: EMPTY_STRING,
  billingCity: EMPTY_STRING,
  billingState: EMPTY_STRING,
  billingZipCode: EMPTY_STRING,
  billingCountry: EMPTY_STRING,
  tag: EMPTY_STRING,

  typeLinkQuickBook: EMPTY_STRING,
  quickBookCustomerId: EMPTY_STRING,
  syncToken: EMPTY_STRING,
};

export const defaultValuesAdditionalContact: IAdditionalContact = {
  name: EMPTY_STRING,
  email: EMPTY_STRING,
  phoneNumber: EMPTY_STRING,
};

export const defaultValuesAddress: IAddressCustomer = {
  address: EMPTY_STRING,
  address2: EMPTY_STRING,
  city: EMPTY_STRING,
  state: EMPTY_STRING,
  zipCode: EMPTY_STRING,
  country: EMPTY_STRING,
  isBillingAddress: false,
};
